import React, { useEffect, useState } from 'react';
import { useConsentContext } from '@/contexts/ConsentContext';
import { ConsentModal } from './ConsentModal';

/**
 * GlobalConsentManager - Single Point of Consent Control
 * 
 * This component manages consent checking at the application level,
 * ensuring the consent modal appears only once per session regardless
 * of navigation between protected routes.
 * 
 * Key Features:
 * - Single consent check for the entire application
 * - Session-persistent modal tracking
 * - No per-route ConsentGuard instances needed
 * - Clean separation of concerns
 */
export function GlobalConsentManager() {
  const {
    consentStatus,
    isLoading,
    checkPurposes,
    refreshConsentStatus
  } = useConsentContext();

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasCheckedConsent, setHasCheckedConsent] = useState(false);

  // Define the AI consent purposes required for the application
  const requiredAIPurposes = ['ai_symptom_assessment', 'clinical_ai_assistance'];

  console.log('🔍 GLOBAL_CONSENT_MANAGER:', {
    action: 'RENDER',
    isLoading,
    hasCheckedConsent,
    showConsentModal,
    timestamp: new Date().toISOString()
  });

  // Single consent check when the application loads
  useEffect(() => {
    const performGlobalConsentCheck = async () => {
      if (isLoading || hasCheckedConsent) {
        return;
      }

      console.log('🔍 GLOBAL_CONSENT_CHECK:', {
        action: 'START',
        requiredPurposes: requiredAIPurposes,
        timestamp: new Date().toISOString()
      });

      try {
        const result = await checkPurposes(requiredAIPurposes);
        
        console.log('🔍 GLOBAL_CONSENT_CHECK:', {
          action: 'RESULT',
          hasValidConsent: result.hasValidConsent,
          missingPurposes: result.missingPurposes,
          timestamp: new Date().toISOString()
        });

        // Show modal if consent is missing from database (persistent approach)
        if (!result.hasValidConsent) {
          console.log('🔍 GLOBAL_CONSENT_MODAL:', {
            action: 'SHOW',
            reason: 'missing_consent_persistent',
            missingPurposes: result.missingPurposes,
            timestamp: new Date().toISOString()
          });
          
          setShowConsentModal(true);
        } else {
          console.log('🔍 GLOBAL_CONSENT_MODAL:', {
            action: 'SKIP',
            reason: 'valid_consent_exists',
            timestamp: new Date().toISOString()
          });
        }

        setHasCheckedConsent(true);
      } catch (error) {
        console.error('🔍 GLOBAL_CONSENT_CHECK:', {
          action: 'ERROR',
          error: error.message,
          timestamp: new Date().toISOString()
        });
        setHasCheckedConsent(true);
      }
    };

    performGlobalConsentCheck();
  }, [isLoading, checkPurposes, hasCheckedConsent]);

  const handleConsentComplete = async (consentId: string) => {
    console.log('🔍 GLOBAL_CONSENT_COMPLETE:', {
      consentId,
      timestamp: new Date().toISOString()
    });
    
    setShowConsentModal(false);
    await refreshConsentStatus();
  };

  const handleConsentDeclined = () => {
    console.log('🔍 GLOBAL_CONSENT_DECLINED:', {
      timestamp: new Date().toISOString()
    });
    
    setShowConsentModal(false);
    // Note: User can still use non-AI features
  };

  // Render the consent modal if needed
  if (showConsentModal) {
    return (
      <ConsentModal
        isOpen={true}
        onClose={handleConsentDeclined}
        requiredPurposes={requiredAIPurposes}
        onConsentComplete={handleConsentComplete}
        onConsentDeclined={handleConsentDeclined}
      />
    );
  }

  // This component doesn't render any visible UI when not showing the modal
  return null;
}
